import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchQuote, useCatalog, useOrganizations } from "../../hooks/queries";
import { useLocale } from "../../hooks/useLocale";
import { localName, money } from "../../lib/format";
import type { Locale, Quote } from "../../lib/types";
import { SectionHeader } from "../ui/Badges";
import { DownloadButton } from "../ui/DownloadButton";

/** School / charity quotation: live price, downloadable as an A4 PDF in Arabic or English. */
export function QuoteGenerator() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const catalog = useCatalog();
  const orgs = useOrganizations();

  const [orgId, setOrgId] = useState("");
  const [recipient, setRecipient] = useState("");
  const [pkgId, setPkgId] = useState("");
  const [students, setStudents] = useState(40);
  const [supervisors, setSupervisors] = useState(5);
  const [docLocale, setDocLocale] = useState<Locale>(locale);
  const [notes, setNotes] = useState("");
  const [quote, setQuote] = useState<Quote | null>(null);

  const packages = useMemo(() => (catalog.data ?? []).filter((p) => p.kind === "school_package").sort((a, b) => a.price - b.price), [catalog.data]);
  useEffect(() => {
    if (!pkgId && packages[1]) setPkgId(packages[1].id);
  }, [packages, pkgId]);
  useEffect(() => {
    setDocLocale(locale);
  }, [locale]);

  useEffect(() => {
    if (!pkgId || students < 1) {
      setQuote(null);
      return;
    }
    const h = setTimeout(() => fetchQuote({ type: "school_trip", packageProductId: pkgId, kidsCount: students, addons: [] }).then(setQuote, () => setQuote(null)), 250);
    return () => clearTimeout(h);
  }, [pkgId, students]);

  const hasRecipient = !!orgId || recipient.trim().length >= 2;
  const body = { type: "school_trip", packageProductId: pkgId, kidsCount: students, supervisors, organizationId: orgId || undefined, recipientName: orgId ? undefined : recipient.trim(), notes: notes.trim() || undefined, locale: docLocale };

  return (
    <section className="card space-y-4 p-4 sm:p-5" aria-label={t("quote.title")}>
      <SectionHeader icon="📝" title={t("quote.title")} right={<span className="rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-[10px] font-extrabold text-cosmic-purple">{t("quote.badge")}</span>} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <label className="label" htmlFor="q-org">{t("quote.organization")}</label>
          <select id="q-org" className="field" value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            <option value="">{t("quote.otherRecipient")}</option>
            {orgs.data?.map((o) => <option key={o.id} value={o.id}>{localName(o, locale)}</option>)}
          </select>
          {!orgId && <input className="field mt-2" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder={t("quote.recipientName")} aria-label={t("quote.recipientName")} />}
        </div>
        <div>
          <label className="label" htmlFor="q-pkg">{t("quote.package")}</label>
          <select id="q-pkg" className="field" value={pkgId} onChange={(e) => setPkgId(e.target.value)}>
            {packages.map((p) => <option key={p.id} value={p.id}>{localName(p, locale)} — {money(p.price)}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-4">
        <div><label className="label" htmlFor="q-n">{t("quote.students")}</label><input id="q-n" className="field num font-mono" type="number" min={1} value={students} onChange={(e) => setStudents(Number(e.target.value))} /></div>
        <div><label className="label" htmlFor="q-s">{t("quote.supervisors")}</label><input id="q-s" className="field num font-mono" type="number" min={0} value={supervisors} onChange={(e) => setSupervisors(Number(e.target.value))} /></div>
        <div className="flex items-center justify-between rounded-xl border border-purple-200 bg-purple-50/60 p-2.5 sm:col-span-2">
          <span className="text-xs font-black text-gray-700">{t("quote.calculated")}</span>
          <span className="num font-mono text-base font-black text-cosmic-purple">{quote ? money(quote.total) : "—"} {t("currency")}</span>
        </div>
      </div>
      <div>
        <label className="label" htmlFor="q-notes">{t("quote.notes")}</label>
        <input id="q-notes" className="field" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={600} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label={t("quote.documentLanguage")} className="flex rounded-xl bg-gray-100 p-1 text-[11px] font-black">
          {(["ar", "en"] as Locale[]).map((l) => <button key={l} type="button" aria-pressed={docLocale === l} onClick={() => setDocLocale(l)} className={`rounded-lg px-3 py-1.5 ${docLocale === l ? "bg-cosmic-purple text-white" : "text-gray-600"}`}>{l === "ar" ? "العربية" : "English"}</button>)}
        </div>
        <DownloadButton path="/documents/quotation" method="POST" body={body} fallbackName="Quotation.pdf" disabled={!pkgId || !hasRecipient || students < 1} className="btn-primary flex-1 !py-2.5">
          📄 {t("quote.download")}
        </DownloadButton>
      </div>
    </section>
  );
}
